import {
  getGridNumericOperators,
  GridColDef,
  GridFilterModel,
} from "@mui/x-data-grid";
import {
  useCalculateBillingsQuery,
  CalculateBillingsApiArg,
} from "../../../state/rtk-query/state/terms";
import {
  CardStyled,
  QuickbooksLoginModal,
  ServerDataGrid,
  SortableButton,
  ToggleDropdown,
} from "../../../components";
import RowsSelectedBanner from "../../../components/cards/RowsSelectedBanner";
import { Button, Stack } from "@mui/material";
import { useQuery, useRtkQueryParams } from "../../../components/hooks";
import { useRowSelectionModel } from "../../../components/hooks/useRowSelectionModel";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInvoiceClientsMutation } from "../../../state/rtk-query/state/quickbooks";
import { convertToHoursAndMinutes, formatDate, isDate } from "../../../lib";
import { Navigation } from "../../../lib";
import { InternalPages } from "..";

const columns: GridColDef[] = [
  { field: "name", headerName: "Organization", flex: 1.5 },
  {
    field: "last_billed",
    headerName: "Last Billed",
    flex: 1,
    type: "date",
    valueFormatter: (v) =>
      v?.value && isDate(v.value)
        ? formatDate(new Date(v.value), "MM/dd/yyyy · h:mm aaa")
        : null,
  },
  {
    field: "total_hours",
    headerName: "CW Hours",
    type: "number",
    valueFormatter: (v) =>
      `${v.value.toFixed(3)} | ${convertToHoursAndMinutes(v.value * 60, true)}`,
    flex: 1,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "billable_hours",
    headerName: "CW Billable Hours",
    type: "number",
    valueFormatter: (v) =>
      `${v.value.toFixed(3)} | ${convertToHoursAndMinutes(v.value * 60, true)}`,
    flex: 1,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "cost",
    headerName: "Approximate CW Cost",
    type: "number",
    valueFormatter: (v) => v.value.toFixed(2),
    flex: 1,
    align: "left",
    headerAlign: "left",
  },
  {
    field: "quickbooks_customer_id",
    headerName: "Linked to quickbooks",
    width: 250,
    align: "center",
    headerAlign: "center",
    type: "boolean",
    filterOperators: getGridNumericOperators()
      .filter(
        (operator) =>
          operator.value === "isEmpty" || operator.value === "isNotEmpty",
      )
      .map((operator) => {
        return {
          ...operator,
        };
      }),
  },
];

export function BillingPage() {
  const [rtkParams, setRtkParams] = useRtkQueryParams();

  const [_params, setParams] = useQuery<Partial<CalculateBillingsApiArg>>(
    "dateRange",
    "billingStatus",
    "activityType",
    "billingDateRange",
  );

  const { dateRange, billingStatus, activityType, billingDateRange } = _params;
  const params = useMemo(
    () => ({
      ...rtkParams,
      billingStatus: billingStatus || "reviewed",
      dateRange,
      billingDateRange,
      activityType: activityType || "custom_work",
    }),
    [rtkParams, billingStatus, dateRange, billingDateRange, activityType],
  );
  const { data, isFetching } = useCalculateBillingsQuery(params, {
    refetchOnMountOrArgChange: true,
  });

  const haveQbIds = data?.rows?.filter((r) => r.quickbooks_customer_id);

  const { onCellClick, onColumnHeaderClick, selectedRows, setSelectedRows } =
    useRowSelectionModel(haveQbIds);

  const [openQb, setOpenQb] = useState(false);

  const [invoiceClients] = useInvoiceClientsMutation();

  const [filterModel, onFilterModelChange] = useState({
    items:
      billingStatus === "billing"
        ? []
        : [{ field: "billable_hours", operator: ">", value: 0 }],
  } as GridFilterModel);

  const handleRowClick = useCallback((row: any) => {
    Navigation.go(InternalPages.adminTimer.path, {
      query: {
        view: "table",
        org_name: row.row?.name,
      },
    });
  }, []);

  const BillingTable = useMemo(
    () => (
      <ServerDataGrid
        key={billingStatus}
        columns={columns}
        rows={data?.rows ?? []}
        rowCount={data?.count ?? 0}
        setQueryParams={setRtkParams}
        currentParams={rtkParams}
        isFetching={isFetching}
        toolbarProps={{ hideToolbar: true }}
        props={{
          checkboxSelection: billingStatus === "reviewed",
          rowSelectionModel: selectedRows,
          onCellClick: onCellClick,
          onColumnHeaderClick: onColumnHeaderClick,
          onRowClick: handleRowClick,
          onFilterModelChange: onFilterModelChange,
          filterModel: filterModel,
          isRowSelectable: (p) => !!haveQbIds?.some((r) => r.id === p.row.id),
        }}
      />
    ),
    [
      billingStatus,
      data?.rows,
      data?.count,
      setRtkParams,
      rtkParams,
      isFetching,
      selectedRows,
      onCellClick,
      onColumnHeaderClick,
      handleRowClick,
      filterModel,
      haveQbIds,
    ],
  );
  useEffect(() => {
    if (!billingStatus) {
      setParams({
        billingStatus: "reviewed",
      });
    }
  }, [billingStatus, setParams]);
  return (
    <>
      <Stack direction={"row"} width={"100%"} overflow={"auto"} gap={3} pb={3}>
        <SortableButton
          title="Timer Date Range"
          onChange={(range) => {
            setParams({
              dateRange: range[0] && range[1] ? range : undefined,
            });
          }}
          isDropDown
          isDate
          currentSort={dateRange as any as Date[]}
          buttonPadding={0}
        />
        <SortableButton
          title="Billed Date Range"
          onChange={(range) => {
            setParams({
              billingDateRange: range[0] && range[1] ? range : undefined,
            });
          }}
          isDropDown
          isDate
          currentSort={billingDateRange as any as Date[]}
          buttonPadding={0}
        />
        <SortableButton
          title="Activity"
          onClick={(val, checked) => {
            setParams({
              activityType: (checked
                ? val.toLowerCase().split(" ").join("_")
                : undefined) as typeof activityType,
            });
          }}
          items={[
            "Custom Work",
            "Standardized Custom Work",
            "Complimentary Custom Work",
          ]}
          isDropDown
          isSingleSelect
          singleSelectSort={
            activityType
              ? activityType
                  .split("_")
                  .map((s) => s[0].toUpperCase() + s.slice(1))
                  .join(" ")
              : "Custom Work"
          }
          buttonPadding={0}
        />
        <ToggleDropdown
          options={[
            { label: "billed", value: "billed" },
            { label: "unbilled", value: "reviewed" },
            { label: "billing", value: "billing" },
          ]}
          setValue={(val) =>
            setParams({
              billingStatus:
                (val as NonNullable<typeof billingStatus>) || "reviewed",
            })
          }
          value={billingStatus || "reviewed"}
          buttonPadding={0}
        />
      </Stack>
      {billingStatus === "reviewed" && (
        <RowsSelectedBanner
          selectedRowsCt={selectedRows.length}
          onClear={() => setSelectedRows([])}
          onSelectAll={() => setSelectedRows(haveQbIds?.map((r) => r.id) || [])}
        >
          <Button
            color="info"
            sx={{ whiteSpace: "nowrap" }}
            onClick={() => {
              setOpenQb(true);
            }}
            disabled={activityType && activityType !== "custom_work"}
          >
            Invoice client
          </Button>
        </RowsSelectedBanner>
      )}
      <CardStyled sx={{ height: "100%", mb: 2 }}>{BillingTable}</CardStyled>
      <QuickbooksLoginModal
        open={openQb}
        apiCall={() =>
          invoiceClients({ body: { orgs: selectedRows, dateRange } })
        }
        handleClose={() => {
          setOpenQb(false);
          setSelectedRows([]);
        }}
      />
    </>
  );
}
